import { ReactNode, createContext, useContext, useState, useCallback } from "react";
import { KeyTrigger, KBKey, AdvKeyInfo, CalibrationKeyData } from "../types/types";

// 高级键类型枚举
export enum AdvKeyType {
  ADV_TYPE_DKS = 0x21,
  ADV_TYPE_MT = 0x22,
  ADV_TYPE_TGL = 0x23,
  ADV_TYPE_SOCD = 0x24,
  ADV_TYPE_RS = 0x25,
  ADV_TYPE_MPT = 0x26,
  ADV_TYPE_END = 0x27
}

// 触发键程配置接口
export interface TriggerConfig {
  min: number;
  max: number;
  default: number;
  step: number;
}

// 死区配置接口
export interface DeadZoneConfig {
  min: number;
  max: number;
  default: number;
  step: number;
}

// RT模式配置接口
export interface RTModeConfig {
  min: number;
  max: number;
  default: number;
  step: number;
}

// 定义上下文类型
export interface AdvKeyProps {
  // 触发键程配置
  triggerConfig: TriggerConfig;
  setTriggerConfig: (config: Partial<TriggerConfig>) => void;
  resetTriggerConfig: () => void;

  // 死区配置
  deadZoneConfig: DeadZoneConfig;
  setDeadZoneConfig: (config: Partial<DeadZoneConfig>) => void;
  resetDeadZoneConfig: () => void;

  // RT模式配置
  rtModeConfig: RTModeConfig;
  setRtModeConfig: (config: Partial<RTModeConfig>) => void;
  resetRtModeConfig: () => void;

  // 按键键程
  keyTrigger: KeyTrigger[];
  setKeyTrigger: (trigger: KeyTrigger[]) => void;

  // 更新单个按键键程
  updateKeyTrigger: (index: number, trigger: Partial<KeyTrigger>) => void;

  // 重置按键键程
  resetKeyTrigger: () => void;

  // 校准数据 - 128个按键的校准数据数组
  calibrationData: (CalibrationKeyData | null)[];
  setCalibrationData: (data: (CalibrationKeyData | null)[]) => void;

  // 更新单个按键的校准数据
  updateKeyCalibrationData: (keyCode: number, data: CalibrationKeyData) => void;

  // 根据HID更新按键的校准数据
  updateKeyCalibrationDataByHID: (hid: number, data: CalibrationKeyData) => void;

  // 获取按键的校准数据
  getKeyCalibrationData: (keyCode: number) => CalibrationKeyData | null;

  // 根据HID获取按键的校准数据
  getKeyCalibrationDataByHID: (hid: number) => CalibrationKeyData | null;

  // 根据HID查找按键索引
  findKeyIndexByHID: (hid: number) => number;

  // 批量更新校准数据
  batchUpdateCalibrationData: (dataList: CalibrationKeyData[]) => void;

  // 清空校准数据
  clearCalibrationData: () => void;

  // 校准状态
  isCalibrating: boolean;
  setIsCalibrating: (calibrating: boolean) => void;

  // 高级键索引1
  advKeyIndex1: KBKey;
  setAdvKeyIndex1: (index: KBKey) => void;
  // 高级键索引2
  advKeyIndex2: KBKey;
  setAdvKeyIndex2: (index: KBKey) => void;

  // 高级键模式
  currentAdvKeyMode: string | null;
  setCurrentAdvKeyMode: (mode: string | null) => void;

  // 高级键
  advKeyInfo: AdvKeyInfo[];
  setAdvKeyInfo: (info: AdvKeyInfo[]) => void;

  // 重置高级键选择
  resetAdvKeySelection: () => void;

  // 检查是否为高级键模式
  isAdvKeyMode: () => boolean;

  // 检查是否为单按键模式
  isSingleKeyMode: () => boolean;
}

// 创建上下文，提供默认值
export const AdvKeyContext = createContext<AdvKeyProps>({
  // 触发键程配置
  triggerConfig: { min: 1, max: 3500, default: 1500, step: 1 },
  setTriggerConfig: () => {},
  resetTriggerConfig: () => {},

  // 死区配置
  deadZoneConfig: { min: 0, max: 2550, default: 200, step: 1 },
  setDeadZoneConfig: () => {},
  resetDeadZoneConfig: () => {},

  // RT模式配置
  rtModeConfig: { min: 1, max: 3500, default: 100, step: 1 },
  setRtModeConfig: () => {},
  resetRtModeConfig: () => {},

  // 按键键程
  keyTrigger: [],
  setKeyTrigger: () => {},

  // 更新单个按键键程
  updateKeyTrigger: () => {},

  // 重置按键键程
  resetKeyTrigger: () => {},

  // 校准数据 - 128个按键的校准数据数组
  calibrationData: new Array(128).fill(null),
  setCalibrationData: () => {},

  // 更新单个按键的校准数据
  updateKeyCalibrationData: () => {},

  // 根据HID更新按键的校准数据
  updateKeyCalibrationDataByHID: () => {},

  // 获取按键的校准数据
  getKeyCalibrationData: () => null,

  // 根据HID获取按键的校准数据
  getKeyCalibrationDataByHID: () => null,

  // 根据HID查找按键索引
  findKeyIndexByHID: () => -1,

  // 批量更新校准数据
  batchUpdateCalibrationData: () => {},

  // 清空校准数据
  clearCalibrationData: () => {},

  // 校准状态
  isCalibrating: false,
  setIsCalibrating: () => {},

  // 高级键索引1
  advKeyIndex1: { row: -1, col: -1, index: -1, code: 0, name: "" },
  setAdvKeyIndex1: () => {},
  advKeyIndex2: { row: -1, col: -1, index: -1, code: 0, name: "" },
  setAdvKeyIndex2: () => {},

  // 高级键模式
  currentAdvKeyMode: null,
  setCurrentAdvKeyMode: () => {},

  // 高级键
  advKeyInfo: [],
  setAdvKeyInfo: () => {},

  // 重置高级键选择
  resetAdvKeySelection: () => {},

  // 检查是否为高级键模式
  isAdvKeyMode: () => false,

  // 检查是否为单按键模式
  isSingleKeyMode: () => false,
});

// 自定义 Hook
export function useAdvKeyboard(): AdvKeyProps {
  const context = useContext(AdvKeyContext);
  if (!context) {
    throw new Error('useAdvKeyboard must be used within an AdvKeyProvider');
  }
  return context;
}

// 常量定义
const MAX_KEYS = 128;

// 默认触发键程配置
const DEFAULT_TRIGGER_CONFIG: TriggerConfig = {
  min: 1,        // 最小触发键程值
  max: 3500,     // 最大触发键程值
  default: 1500, // 默认触发键程值
  step: 1,       // 按钮步进值：1 = 0.001mm
};

// 默认死区配置
const DEFAULT_DEADZONE_CONFIG: DeadZoneConfig = {
  min: 0,        // 最小死区值
  max: 2550,     // 最大死区值（2.550mm）
  default: 200,  // 默认死区值（0.200mm）
  step: 1,       // 按钮步进值：1 = 0.001mm
};

// 默认RT模式配置
const DEFAULT_RTMODE_CONFIG: RTModeConfig = {
  min: 1,        // 最小RT值
  max: 3500,     // 最大RT值（3.500mm）
  default: 100,  // 默认RT值（0.100mm）
  step: 1,       // 按钮步进值：1 = 0.001mm
};

// 高级键模式常量
const SINGLE_KEY_MODES = ['DKS', 'MT', 'TGL', 'END', 'MPT']; // 单按键模式
const DUAL_KEY_MODES = ['RS', 'SOCD']; // 双按键模式

// 辅助函数：根据模式名称获取枚举值
export const getAdvKeyTypeByMode = (mode: string | null): number | null => {
  if (!mode) return null;
  
  switch (mode.toUpperCase()) {
    case 'DKS':
      return 0x21; // AdvKeyType.DKS
    case 'MT':
      return 0x22; // AdvKeyType.MT
    case 'TGL':
      return 0x23; // AdvKeyType.TGL
    case 'SOCD':
      return 0x24; // AdvKeyType.SOCD
    case 'RS':
      return 0x25; // AdvKeyType.RS
    case 'MPT':
      return 0x26; // AdvKeyType.MPT
    case 'END':
      return 0x27; // AdvKeyType.END
    default:
      return null;
  }
};

// 辅助函数：根据枚举值获取模式名称
export const getModeByAdvKeyType = (advType: number): string => {
  switch (advType) {
    case 0x21: // AdvKeyType.DKS
      return 'DKS';
    case 0x22: // AdvKeyType.MT
      return 'MT';
    case 0x23: // AdvKeyType.TGL
      return 'TGL';
    case 0x24: // AdvKeyType.SOCD
      return 'SOCD';
    case 0x25: // AdvKeyType.RS
      return 'RS';
    case 0x26: // AdvKeyType.MPT
      return 'MPT';
    case 0x27: // AdvKeyType.END
      return 'END';
    default:
      return '';
  }
};

// 初始化按键键程数据
const initializeKeyTrigger = (defaultValue: number = DEFAULT_TRIGGER_CONFIG.default): KeyTrigger[] => {
  return new Array(MAX_KEYS).fill(null).map((_, index) => ({
    profile: 0,
    row: 0,
    col: 0,
    keyIndex: index,
    keyCode: 0,
    switchType: 0,
    keyMode: 1,
    keyActuation: defaultValue, // 使用传入的默认值
    rtPress: 100,
    rtRelease: 100,
    deadzoneStatus: false,
    pressDeadzone: 100,
    releaseDeadzone: 100,
    pressPrecision: 0,
    releasePrecision: 0,
  }));
};

// Provider 组件
function AdvKeyProvider({ children }: { children: ReactNode }) {
  // 触发键程配置状态
  const [triggerConfig, setTriggerConfigState] = useState<TriggerConfig>(DEFAULT_TRIGGER_CONFIG);

  // 更新触发键程配置
  const setTriggerConfig = useCallback((config: Partial<TriggerConfig>) => {
    setTriggerConfigState(prev => ({ ...prev, ...config }));
  }, []);

  // 重置触发键程配置
  const resetTriggerConfig = useCallback(() => {
    setTriggerConfigState(DEFAULT_TRIGGER_CONFIG);
  }, []);

  // 死区配置状态
  const [deadZoneConfig, setDeadZoneConfigState] = useState<DeadZoneConfig>(DEFAULT_DEADZONE_CONFIG);

  // 更新死区配置
  const setDeadZoneConfig = useCallback((config: Partial<DeadZoneConfig>) => {
    setDeadZoneConfigState(prev => ({ ...prev, ...config }));
  }, []);

  // 重置死区配置
  const resetDeadZoneConfig = useCallback(() => {
    setDeadZoneConfigState(DEFAULT_DEADZONE_CONFIG);
  }, []);

  // RT模式配置状态
  const [rtModeConfig, setRtModeConfigState] = useState<RTModeConfig>(DEFAULT_RTMODE_CONFIG);

  // 更新RT模式配置
  const setRtModeConfig = useCallback((config: Partial<RTModeConfig>) => {
    setRtModeConfigState(prev => ({ ...prev, ...config }));
  }, []);

  // 重置RT模式配置
  const resetRtModeConfig = useCallback(() => {
    setRtModeConfigState(DEFAULT_RTMODE_CONFIG);
  }, []);

  const [keyTrigger, setKeyTrigger] = useState<KeyTrigger[]>(() => initializeKeyTrigger());

  // 校准数据状态 - 128个按键的校准数据数组
  const [calibrationData, setCalibrationData] = useState<(CalibrationKeyData | null)[]>(() => new Array(128).fill(null));
  const [isCalibrating, setIsCalibrating] = useState<boolean>(false);

  // 更新单个按键的校准数据
  const updateKeyCalibrationData = useCallback((keyCode: number, data: CalibrationKeyData) => {
    setCalibrationData(prev => {
      const newData = [...prev];
      // 根据keyCode找到对应的索引位置
      const index = Math.min(keyCode, 127); // 确保不超过数组范围
      newData[index] = data;
      return newData;
    });
  }, []);

  // 根据HID更新按键的校准数据
  const updateKeyCalibrationDataByHID = useCallback((hid: number, data: CalibrationKeyData) => {
    setCalibrationData(prev => {
      const newData = [...prev];
      // 根据HID找到对应的索引位置
      const index = calibrationData.findIndex(item => item?.keyHID === hid);
      if (index !== -1) {
        newData[index] = data;
      } else {
        // 如果没有找到，找到第一个空位置添加
        const emptyIndex = newData.findIndex(item => item === null);
        if (emptyIndex !== -1) {
          newData[emptyIndex] = data;
        }
      }
      return newData;
    });
  }, [calibrationData]);

  // 获取按键的校准数据
  const getKeyCalibrationData = useCallback((keyCode: number): CalibrationKeyData | null => {
    const index = Math.min(keyCode, 127); // 确保不超过数组范围
    return calibrationData[index];
  }, [calibrationData]);

  // 根据HID获取按键的校准数据
  const getKeyCalibrationDataByHID = useCallback((hid: number): CalibrationKeyData | null => {
    const foundData = calibrationData.find(item => item?.keyHID === hid);
    return foundData || null;
  }, [calibrationData]);

  // 根据HID查找按键索引
  const findKeyIndexByHID = useCallback((hid: number): number => {
    return calibrationData.findIndex(item => item?.keyHID === hid);
  }, [calibrationData]);

  // 批量更新校准数据
  const batchUpdateCalibrationData = useCallback((dataList: CalibrationKeyData[]) => {
    setCalibrationData(prev => {
      const newData = [...prev];
      
      dataList.forEach(item => {
        // 直接使用传入数据的keyIndex作为数组索引
        const targetIndex = item.keyIndex;
        if (targetIndex >= 0 && targetIndex < newData.length) {
          newData[targetIndex] = item;
        }
      });
      
      return newData;
    });
  }, []);

  // 清空校准数据
  const clearCalibrationData = useCallback(() => {
    setCalibrationData(new Array(128).fill(null));
  }, []);

  // 更新单个按键键程
  const updateKeyTrigger = useCallback((index: number, trigger: Partial<KeyTrigger>) => {
    setKeyTrigger(prev => {
      const newTrigger = [...prev];
      newTrigger[index] = { ...newTrigger[index], ...trigger };
      return newTrigger;
    });
  }, []);

  // 重置按键键程
  const resetKeyTrigger = useCallback(() => {
    setKeyTrigger(initializeKeyTrigger());
  }, []);

  // 初始化高级键
  const [advKeyInfo, setAdvKeyInfo] = useState<AdvKeyInfo[]>([]);
  const [advKeyIndex1, setAdvKeyIndex1] = useState<KBKey>({ row: -1, col: -1, index: -1, code: 0, name: "" });
  const [advKeyIndex2, setAdvKeyIndex2] = useState<KBKey>({ row: -1, col: -1, index: -1, code: 0, name: "" });
  const [currentAdvKeyMode, setCurrentAdvKeyMode] = useState<string | null>(null);

  // 重置高级键选择
  const resetAdvKeySelection = useCallback(() => {
    setAdvKeyIndex1({ row: -1, col: -1, index: -1, code: 0, name: "" });
    setAdvKeyIndex2({ row: -1, col: -1, index: -1, code: 0, name: "" });
  }, []);

  // 检查是否为高级键模式
  const isAdvKeyMode = useCallback(() => {
    return currentAdvKeyMode !== null && 
           [...SINGLE_KEY_MODES, ...DUAL_KEY_MODES].includes(currentAdvKeyMode);
  }, [currentAdvKeyMode]);

  // 检查是否为单按键模式
  const isSingleKeyMode = useCallback(() => {
    return currentAdvKeyMode !== null && SINGLE_KEY_MODES.includes(currentAdvKeyMode);
  }, [currentAdvKeyMode]);

  // 当模式改变时，自动处理按键选择
  const handleAdvKeyModeChange = useCallback((mode: string | null) => {
    setCurrentAdvKeyMode(mode);
    
    // 如果是单按键模式，清空第二个按键选择
    if (mode && SINGLE_KEY_MODES.includes(mode)) {
      setAdvKeyIndex2({ row: -1, col: -1, index: -1, code: 0, name: "" });
    }
    
    // 如果退出高级键模式，清空所有选择
    if (!mode) {
      resetAdvKeySelection();
    }
  }, [resetAdvKeySelection]);

  const contextValue: AdvKeyProps = {
    // 触发键程配置
    triggerConfig,
    setTriggerConfig,
    resetTriggerConfig,

    // 死区配置
    deadZoneConfig,
    setDeadZoneConfig,
    resetDeadZoneConfig,

    // RT模式配置
    rtModeConfig,
    setRtModeConfig,
    resetRtModeConfig,

    // 按键键程
    keyTrigger,
    setKeyTrigger,

    // 更新方法
    updateKeyTrigger,

    // 重置方法
    resetKeyTrigger,

    // 校准数据 - 128个按键的校准数据数组
    calibrationData,
    setCalibrationData,

    // 更新单个按键的校准数据
    updateKeyCalibrationData,

    // 根据HID更新按键的校准数据
    updateKeyCalibrationDataByHID,

    // 获取按键的校准数据
    getKeyCalibrationData,

    // 根据HID获取按键的校准数据
    getKeyCalibrationDataByHID,

    // 根据HID查找按键索引
    findKeyIndexByHID,

    // 批量更新校准数据
    batchUpdateCalibrationData,

    // 清空校准数据
    clearCalibrationData,

    // 校准状态
    isCalibrating,
    setIsCalibrating,

    // 高级键索引1
    advKeyIndex1,
    setAdvKeyIndex1,

    // 高级键索引2
    advKeyIndex2,
    setAdvKeyIndex2,

    // 高级键模式
    currentAdvKeyMode,
    setCurrentAdvKeyMode: handleAdvKeyModeChange,

    // 高级键
    advKeyInfo,
    setAdvKeyInfo,

    // 重置高级键选择
    resetAdvKeySelection,

    // 检查函数
    isAdvKeyMode,
    isSingleKeyMode,
  };

  return (
    <AdvKeyContext.Provider value={contextValue}>
      {children}
    </AdvKeyContext.Provider>
  );
}

export default AdvKeyProvider;
